import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectCurrentGroup,
  selectGroupsStatus,
  selectMembers,
} from "../features/members/members-slice";
import {
  fetchGroupById,
  fetchGroupMembers,
  fetchMembers,
  addMemberToGroup,
  removeMemberFromGroup,
} from "../features/members/slice-extensions/members-slice-extension";
import { Member } from "../features/members/models/Member";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import { toast } from "react-toastify";

const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
};

const MEMBER_COLORS = [
  "#367D7D", "#2E6B6B", "#3D8F8F", "#2A7A5E",
  "#4A8C6F", "#3B7A9E", "#2D6E8A", "#458B74",
];

const getMemberColor = (firstName: string, lastName: string): string => {
  return MEMBER_COLORS[hashString(firstName + lastName) % MEMBER_COLORS.length];
};

const GroupDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const group = useAppSelector(selectCurrentGroup);
  const groupsStatus = useAppSelector(selectGroupsStatus);
  const allMembers = useAppSelector(selectMembers);

  const [groupMembers, setGroupMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);

  // Members available to add (not already in the group)
  const availableMembers = useMemo(() => {
    const groupMemberIds = new Set(groupMembers.map((m) => m.id));
    return allMembers.filter((m) => {
      if (groupMemberIds.has(m.id)) return false;
      if (!memberSearchText.trim()) return true;
      const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
      return fullName.includes(memberSearchText.toLowerCase());
    });
  }, [allMembers, groupMembers, memberSearchText]);

  const loadGroupMembers = async () => {
    if (id) {
      setMembersLoading(true);
      try {
        const result = await dispatch(fetchGroupMembers(id));
        if (fetchGroupMembers.fulfilled.match(result)) {
          setGroupMembers(result.payload);
        }
      } finally {
        setMembersLoading(false);
      }
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchGroupById(id));
      dispatch(fetchMembers());
      loadGroupMembers();
    }
  }, [id, dispatch]);

  const handleAddMember = async (memberId: string) => {
    if (!id || isAddingMember) return;
    setIsAddingMember(true);
    try {
      const result = await dispatch(addMemberToGroup({ groupId: id, memberId }));
      if (addMemberToGroup.fulfilled.match(result)) {
        toast.success("Member added to group!");
        setMemberSearchText("");
        setShowMemberDropdown(false);
        // Refresh both group members and all members to keep availableMembers in sync
        await loadGroupMembers();
        dispatch(fetchMembers());
      } else {
        toast.error("Failed to add member.");
      }
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!id) return;
    try {
      const result = await dispatch(removeMemberFromGroup({ groupId: id, memberId }));
      if (removeMemberFromGroup.fulfilled.match(result)) {
        toast.success("Member removed from group.");
        await loadGroupMembers();
        dispatch(fetchMembers());
      } else {
        toast.error("Failed to remove member.");
      }
    } catch {
      toast.error("Failed to remove member.");
    }
  };

  if (groupsStatus === LoadingStatus.Loading) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <div className="text-center py-4">
                  <p className="text-grey-30 dark:text-grey-70">
                    Loading group details...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!group) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <h1 className="text-grey-10 dark:text-white">
                  Group Not Found
                </h1>
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <p className="text-grey-30 dark:text-grey-70 mb-4">
                    The group you are looking for does not exist or has been
                    removed.
                  </p>
                  <button
                    onClick={() => navigate(ROUTES.GROUPS)}
                    className="btn btn-secondary"
                  >
                    Back to Groups
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            <Breadcrumbs />

            <div className="space-y-4 py-6 lg:space-y-6">
              {/* Header */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h1 className="text-grey-10 dark:text-white">{group?.name}</h1>
                <Link
                  to={ROUTES.GROUPS}
                  className="btn btn-secondary flex items-center justify-center gap-2"
                >
                  <svg
                    width={16}
                    height={16}
                    viewBox="6 6 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>Back to Groups</span>
                </Link>
              </div>

              {/* Group Info Card */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                {/* Description */}
                <div className="mb-4">
                  <h3 className="text-grey-10 dark:text-grey-90 mb-2">
                    Description
                  </h3>
                  <p className="text-grey-30 dark:text-grey-70">
                    {group?.description || "No description"}
                  </p>
                </div>

                {/* Tags */}
                {group?.tags && group.tags.length > 0 && (
                  <div>
                    <h3 className="text-grey-10 dark:text-grey-90 mb-2">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {group.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-90 dark:bg-primary-20 text-primary-30 dark:text-primary-90"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <hr className="border-grey-70" />

              {/* Group Members Section */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                <h3 className="text-grey-10 dark:text-grey-90 mb-4">
                  Group Members ({groupMembers.length})
                </h3>

                {/* Add Member Search */}
                <div className="relative mb-4">
                  <label className="block text-sm font-medium text-grey-30 dark:text-white mb-1">
                    Add Member:
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Search members to add..."
                    value={memberSearchText}
                    onChange={(e) => {
                      setMemberSearchText(e.target.value);
                      setShowMemberDropdown(true);
                    }}
                    onFocus={() => setShowMemberDropdown(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => setShowMemberDropdown(false), 200);
                    }}
                    disabled={isAddingMember}
                  />
                  {showMemberDropdown && availableMembers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-lg border border-grey-70 dark:border-primary-20 bg-white dark:bg-primary-10 shadow-lg">
                      {availableMembers.map((member) => {
                        const initials = getInitials(member.firstName, member.lastName);
                        return (
                          <button
                            key={member.id}
                            type="button"
                            className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors"
                            onClick={() => handleAddMember(member.id)}
                          >
                            <div
                              className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold shrink-0"
                              style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                              title={`${member.firstName} ${member.lastName}`}
                            >
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {member.firstName} {member.lastName}
                              </p>
                              {member.email && (
                                <p className="text-xs text-grey-50 dark:text-grey-70 truncate">
                                  {member.email}
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {membersLoading ? (
                  <p className="text-grey-30 dark:text-grey-70">
                    Loading members...
                  </p>
                ) : groupMembers.length === 0 ? (
                  <p className="text-grey-30 dark:text-grey-70">
                    No members in this group yet.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {groupMembers.map((member) => {
                      const fullName = `${member.firstName} ${member.lastName}`;
                      const initials = getInitials(
                        member.firstName,
                        member.lastName
                      );
                      return (
                        <li
                          key={member.id}
                          className="flex items-center gap-3 p-3 rounded-lg bg-grey-95 dark:bg-primary-20"
                        >
                          <div
                            className="flex items-center justify-center w-10 h-10 rounded-full text-white text-sm font-bold shrink-0"
                            style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                            title={fullName}
                          >
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-grey-10 dark:text-grey-90 truncate">
                              {fullName}
                            </p>
                            {member.email && (
                              <p className="text-sm text-grey-30 dark:text-grey-70 truncate">
                                {member.email}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-grey-50 hover:text-red-500 dark:text-grey-70 dark:hover:text-red-400 shrink-0 p-1"
                            title="Remove from group"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <Link
                  to={ROUTES.EDIT_GROUP.replace(":id", group?.id || "")}
                  className="btn btn-primary inline-flex items-center justify-center gap-2"
                >
                  Edit Group
                </Link>
                <button
                  onClick={() => navigate(ROUTES.GROUPS)}
                  className="btn btn-secondary"
                >
                  Back to Groups
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GroupDetailsPage;
